import { useEffect, useState } from "react";
import { useTranslation } from "../i18n";
import { motion, AnimatePresence } from "framer-motion";
import { RiCloseLine, RiDeleteBinLine } from "react-icons/ri";
import type { Job } from "../types/job";
import { jobErrorText } from "../lib/jobError";
import api from "../lib/api";

interface PodcastPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  onApprove: (jobId: Job) => Promise<void>;
  onDecline: (jobId: string) => Promise<void>;
  onDismiss?: (jobId: string) => Promise<void>;
  showHistory?: boolean;
}

export default function PodcastPreviewModal({
  isOpen,
  onClose,
  job,
  onApprove,
  onDecline,
  onDismiss,
  showHistory = false,
}: PodcastPreviewModalProps) {
  const { t, i18n } = useTranslation();
  const [isApproving, setIsApproving] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [currentJob, setCurrentJob] = useState<Job>(job);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const getJobDisplayName = (name: string | null | undefined) => {
    if (!name) return t("modal.podcastGeneration");
    const key = `jobNames.${name.toLowerCase().replace(/\s+/g, "_")}`;
    return i18n.exists(key) ? t(key) : name;
  };

  // ✅ Modal açıldığında job detaylarını API'den çek
  useEffect(() => {
    if (!isOpen || !job.id) return;

    const fetchJobDetails = async () => {
      try {
        setIsLoadingDetails(true);

        const response = await api.get<Job>(`/${job.id}`);

        setCurrentJob(response.data);
      } catch (error) {
        console.error(`[PodcastPreviewModal] ❌ Failed to fetch job details:`, error);
        // Hata durumunda mevcut job'ı kullan
        setCurrentJob(job);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchJobDetails();
  }, [isOpen, job.id]);

  // Esc closes the modal (same callback as the X). Only attached while mounted.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      await onApprove(currentJob);
      onClose();
    } finally {
      setIsApproving(false);
    }
  };

  const handleDecline = async () => {
    try {
      setIsDeclining(true);
      await onDecline(currentJob.id);
      onClose();
    } finally {
      setIsDeclining(false);
    }
  };

  const handleDismiss = async () => {
    if (onDismiss) {
      await onDismiss(currentJob.id);
      onClose();
    }
  };

  // Try to find podcast data in different possible locations
  const podcastScript =
    currentJob.result?.podcastScript ||
    currentJob.result ||
    (currentJob as any).podcastScript ||
    (currentJob as any).data?.podcastScript ||
    (currentJob as any).data ||
    (currentJob as any).output ||
    (currentJob as any).response;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-60"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] max-h-[85vh] rounded-3xl border border-white/20 bg-white/95 backdrop-blur-xl shadow-2xl z-70 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-fuchsia-500/5 pointer-events-none" />

            <div className="relative">
              <div className="flex items-center justify-between p-5 border-b border-black/5">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">
                    {getJobDisplayName(currentJob.name)}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {t("jobCard.scene")} {currentJob.sceneIndex ?? "-"}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {onDismiss && !showHistory && (
                    <button
                      onClick={handleDismiss}
                      className="p-2 hover:bg-red-50 rounded-xl transition-colors"
                      title={t("actions.dismiss")}
                    >
                      <RiDeleteBinLine className="w-5 h-5 text-gray-600 hover:text-red-600" />
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-black/5 rounded-xl transition-colors"
                  >
                    <RiCloseLine className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="p-5 max-h-[calc(85vh-140px)] overflow-y-auto">
                {isLoadingDetails ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
                      <svg
                        className="w-8 h-8 text-purple-600 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                    <p className="text-gray-600 font-medium">{t("podcast.loadingDetails")}</p>
                    <p className="text-sm text-gray-500 mt-2">{t("podcast.pleaseWait")}</p>
                  </div>
                ) : currentJob.status === "failed" ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                      <svg
                        className="w-8 h-8 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-red-600 font-bold text-lg mb-2">{t("widget.failed")}</p>
                    <p className="text-sm text-gray-600 mb-4">{t("podcast.scriptFailed")}</p>
                    {currentJob.error && (
                      <div className="mx-auto max-w-md bg-red-50 border border-red-200 rounded-xl p-4">
                        <p className="text-xs font-semibold text-red-800 mb-1">
                          {t("jobCard.error")}:
                        </p>
                        <p className="text-sm text-red-700 break-words">
                          {jobErrorText(currentJob, t)}
                        </p>
                      </div>
                    )}
                  </div>
                ) : podcastScript?.sections ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-purple-50/80 to-fuchsia-50/80 border border-purple-100/50">
                      <svg
                        className="w-5 h-5 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <h3 className="text-sm font-semibold text-purple-900">
                        {t("podcast.scriptReady")}
                      </h3>
                    </div>

                    <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-5 border border-white/40 shadow-sm">
                      <div className="text-center border-b border-gray-200/50 pb-4 mb-5">
                        <h2 className="text-xl font-bold text-gray-900">
                          {podcastScript.podcast_name || t("podcast.podcast")}
                        </h2>
                        <h3 className="text-sm text-gray-600 mt-1">
                          {podcastScript.episode_title || t("podcast.episode")}
                        </h3>
                      </div>

                      <div className="space-y-5 max-h-[400px] overflow-y-auto pr-2">
                        {podcastScript.sections.map((section: any, sectionIndex: number) => (
                          <div key={sectionIndex}>
                            <h4 className="font-bold text-gray-800 mb-3 text-xs uppercase tracking-wide flex items-center gap-2">
                              <span className="w-1 h-4 bg-gradient-to-b from-purple-500 to-fuchsia-500 rounded-full"></span>
                              {section.title}
                            </h4>
                            <div className="space-y-2.5 pl-3">
                              {Array.isArray(section.content) &&
                                section.content.map((item: any, itemIndex: number) => {
                                  if (Array.isArray(item)) {
                                    return (
                                      <div key={itemIndex} className="space-y-2.5">
                                        {item.map((dialogue: any, dialogueIndex: number) => {
                                          const isHost =
                                            dialogue.speaker === podcastScript.host_info?.name;
                                          const genderInfo = isHost
                                            ? podcastScript.host_info?.gender
                                            : podcastScript.guest_co_host_info?.gender;
                                          const isFemale =
                                            genderInfo === true ||
                                            String(genderInfo).toLowerCase() === "female";

                                          return (
                                            <div
                                              key={dialogueIndex}
                                              className="flex gap-3 p-2.5 rounded-xl hover:bg-white/50 transition-colors"
                                            >
                                              <div
                                                className={`shrink-0 w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold shadow-sm ${
                                                  isFemale
                                                    ? "bg-gradient-to-br from-pink-100 to-pink-50 text-pink-700 border border-pink-200/50"
                                                    : "bg-gradient-to-br from-blue-100 to-blue-50 text-blue-700 border border-blue-200/50"
                                                }`}
                                              >
                                                {dialogue.speaker?.charAt(0)}
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <span className="font-semibold text-xs text-gray-800">
                                                  {dialogue.speaker}
                                                </span>
                                                <p className="text-sm text-gray-700 mt-0.5 leading-relaxed">
                                                  {dialogue.text}
                                                </p>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  } else if (item.speaker && item.text) {
                                    const isHost = item.speaker === podcastScript.host_info?.name;
                                    const genderInfo = isHost
                                      ? podcastScript.host_info?.gender
                                      : podcastScript.guest_co_host_info?.gender;
                                    const isFemale =
                                      genderInfo === true ||
                                      String(genderInfo).toLowerCase() === "female";

                                    return (
                                      <div
                                        key={itemIndex}
                                        className="flex gap-3 p-2.5 rounded-xl hover:bg-white/50 transition-colors"
                                      >
                                        <div
                                          className={`shrink-0 w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold shadow-sm ${
                                            isFemale
                                              ? "bg-gradient-to-br from-pink-100 to-pink-50 text-pink-700 border border-pink-200/50"
                                              : "bg-gradient-to-br from-blue-100 to-blue-50 text-blue-700 border border-blue-200/50"
                                          }`}
                                        >
                                          {item.speaker?.charAt(0)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <span className="font-semibold text-xs text-gray-800">
                                            {item.speaker}
                                          </span>
                                          <p className="text-sm text-gray-700 mt-0.5 leading-relaxed">
                                            {item.text}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
                      <svg
                        className="w-8 h-8 text-purple-600 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                    <p className="text-gray-600 font-medium">{t("podcast.generatingScript")}</p>
                    <p className="text-sm text-gray-500 mt-2">{t("podcast.takesAMoment")}</p>
                  </div>
                )}

                {!isLoadingDetails && !showHistory && (
                  <div className="flex gap-3 pt-4 mt-4 border-t border-gray-200">
                    <button
                      onClick={handleDecline}
                      disabled={isDeclining || isApproving}
                      className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isDeclining ? t("actions.declining") : t("actions.decline")}
                    </button>

                    {currentJob?.status === "completed" && (
                      <button
                        onClick={handleApprove}
                        disabled={isApproving || isDeclining}
                        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-500/25"
                      >
                        {isApproving ? t("actions.approving") : t("actions.approve")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
